import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { User } from "../models/user";
import {
  Workspace,
  WorkspaceMember,
  WorkspaceSummary,
} from "../models/workspace";
import { AnalyticsDestination, AnalyticsIdentity } from "../models/analytics";
import { fetchFastApi } from "../api/fastapi";
import { resetMetrics } from "../lib/metrics";
import { suppressOptroAutoSignIn } from "../lib/optroReferrer";

export interface AuthState {
  workspace: Workspace | null;
  user: User | null;
  workspaceMember: WorkspaceMember | null;
  workspaces: WorkspaceSummary[];
  // The Amplitude project this session reports to, or null when there is no
  // workspace to route for. Required rather than optional: a call site that
  // forgets it leaves metrics inert for that session, and the compiler is the
  // only thing that reliably notices.
  analyticsDestination: AnalyticsDestination | null;
  // Null falls back to identifying by `user.id`.
  analyticsIdentity: AnalyticsIdentity | null;
  loading?: boolean;
  accessToken?: string | null;
}

interface AuthContextType extends AuthState {
  setAuth: React.Dispatch<React.SetStateAction<AuthState>>;
  logout: () => void;
  getUserInfo: (userId: string) => Promise<User>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [auth, setAuth] = useState<AuthState>({
    user: null,
    loading: true,
    accessToken: null,
    workspace: null,
    workspaceMember: null,
    workspaces: [],
    analyticsDestination: null,
    analyticsIdentity: null,
  });
  const [userInfoMapCache, setUserInfoMapCache] = useState<
    Record<string, User>
  >({});

  const getUserInfo = useCallback(
    async (userId: string) => {
      if (userInfoMapCache[userId]) {
        return userInfoMapCache[userId];
      }
      const userInfo = await fetchFastApi({
        method: "GET",
        path: `/user/user-info/${userId}`,
      });
      setUserInfoMapCache((prev) => ({ ...prev, [userId]: userInfo }));
      return userInfo;
    },
    [userInfoMapCache],
  );

  const logout = useCallback(() => {
    fetchFastApi({
      method: "POST",
      path: "/auth/logout",
      body: {
        refresh_token: localStorage.getItem("refresh_token") || "",
      },
    });

    setAuth({
      user: null,
      loading: false,
      accessToken: null,
      workspace: null,
      workspaceMember: null,
      workspaces: [],
      analyticsDestination: null,
      analyticsIdentity: null,
    });
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("id_token");
    localStorage.removeItem("workspace_id");
    // Logging out is a decision to leave, so /signin must not bounce straight
    // back into Optro and sign the user in again.
    suppressOptroAutoSignIn();
    resetMetrics();
  }, []);

  // Memoize the context value so it only changes when the auth state (or the
  // stable callbacks) actually change. Rebuilding this object every render made
  // every `useAuth()` consumer re-render on each provider render, which let a
  // transient auth flap (e.g. a token-refresh hiccup toggling user null↔set)
  // amplify into a synchronous remount storm across the authenticated subtree.
  const value = useMemo(
    () => ({ ...auth, setAuth, logout, getUserInfo }),
    [auth, setAuth, logout, getUserInfo],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
